import { Form } from 'antd'
import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import { Trans } from '@lingui/react/macro'

import CreateMusicIdeas from '../../homepage/_components/CreateMusic/_components/CreateMusicIdeas'
import style from '../../_components/H2/index.module.scss'

import type { MusicGeneratorFormValuesNew } from '@/pages/tad.ai/ai-music-generator/_types/form'
import type { IdeasRenderParams } from '@/pages/tad.ai/ai-music-generator/_components/Ideas'

import GeneratorLyricsSmart from '@/pages/tad.ai/ai-music-generator/_blocks/GeneratorForm/components/GeneratorLyricsSmart'
import { useInitGeneratorType } from '@/pages/tad.ai/ai-music-generator/_blocks/GeneratorForm/_hooks/initGeneratorType'
import { useMusicFormStore } from '@/pages/tad.ai/ai-music-generator/_store/useMusicFormStore'
import { PageStoreProvider } from '@/pages/tad.ai/_shared/formStore'
import { createMusicFormStore } from '@/pages/tad.ai/ai-music-generator/_store/formStore'


import { cls } from 'tagged-classnames-free';
const Ideas = dynamic(
  () => import('@/pages/tad.ai/ai-music-generator/_components/Ideas'),
  {
    ssr: false,
  },
)

function TopBannerInner() {
  const [form] = Form.useForm<MusicGeneratorFormValuesNew>()
  const formValues = useMusicFormStore((s) => s.form)
  const hasHydrated = useMusicFormStore((s) => s._hasHydrated)
  const setFormValues = useMusicFormStore((s) => s.setFormValues)
  const initializedFormRef = useRef(false)
  const router = useRouter()
  const { onChangeGeneratorType } = useInitGeneratorType()

  useEffect(() => {
    if (!initializedFormRef.current && hasHydrated && formValues) {
      initializedFormRef.current = true
      form.setFieldsValue({
        ...formValues,
      })
    }
  }, [form, formValues, hasHydrated])

  const outwardEvent = (): void => {
    onChangeGeneratorType('auto')
    setFormValues({
      promptTabsMode: 'smart',
    })
    router.push('/ai-music-generator')
  }

  return (
    <div className='relative z-[1] mx-auto max-w-[1206px] px-4 pt-[100px] text-center md:pt-[140px]'>
      <h1
        className={cls`mx-auto ${style.heightLightText} max-w-[1000px] text-2xl font-bold md:text-[64px] md:font-black md:leading-snug`}
      >
        <Trans>Use Tad AI Music Generator to Unleash Music Possibilities</Trans>
      </h1>
      <p className='text-f-other-10 pt-3 text-sm md:text-xl'>
        <Trans>
          Tad AI creates original AI music in any style from simple prompts. Try
          it now!
        </Trans>
      </p>
      <div className='mt-4 md:mt-[30px]'>
        <Form<MusicGeneratorFormValuesNew>
          layout='vertical'
          preserve
          className={cls`
            scrollbar px-6xl:max-w-none mx-auto w-full max-w-[760px] flex-1 xl:overflow-auto
            [&_[class*="form-item-label"]>label]:text-base
            [&_[class*="form-item-label"]>label]:font-semibold
          `}
          form={form}
          initialValues={{
            promptTabsMode: 'smart',
            promptSelectMode: 'smart',
            customTaskType: 'Song',
            taskType: 'Song',
            thinking: true,
          }}
          onValuesChange={(_, values) => {
            const newValues = {
              ...values,
              imageUrl: values.imageUrl?.startsWith('http')
                ? values.imageUrl
                : undefined,
            }
            setFormValues(newValues)
          }}
          scrollToFirstError
        >
          <GeneratorLyricsSmart
            form={form}
            outwardEvent={outwardEvent}
            textareaStyle={{ resize: 'none' }}
            isEntryPage
          />
          <Ideas
            form={form}
            renderFn={(params: IdeasRenderParams) => {
              return <CreateMusicIdeas params={params} />
            }}
          />
        </Form>
      </div>
    </div>
  )
}

export const TopBanner: React.FC = () => {
  return (
    <PageStoreProvider createStore={createMusicFormStore}>
      <TopBannerInner />
    </PageStoreProvider>
  )
}
